import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Calendar, Target, ArrowRight } from 'lucide-react';
import axios from 'axios';
import { PageSEO } from '../components/PageSEO';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ProjectsPage = () => {
  const [projects, setProjects] = useState([]);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/projects`);
      setProjects(response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const filteredProjects = filter === 'all' 
    ? projects 
    : projects.filter(p => p.status === filter);

  const categories = [
    { id: 'all', label: 'Tümü' },
    { id: 'active', label: 'Aktif' },
    { id: 'planned', label: 'Planlanan' },
    { id: 'completed', label: 'Tamamlanan' }
  ];

  return (
    <div data-testid="projects-page">
      <PageSEO title="Çalışmalarımız" description="KOSAD'ın sürdürülebilir arıcılık projeleri, ekosistem koruma ve kadın girişimci destekleme çalışmaları." path="/calismalarimiz" />
      {/* Hero */}
      <section className="relative py-32 bg-[#1A3625]">
        <div className="container-custom">
          <motion.div 
            className="max-w-3xl"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <span className="label-uppercase text-[#D4AF37] mb-4 block">Çalışmalarımız</span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl text-white font-semibold mb-6">
              Projeler ve Faaliyetler
            </h1>
            <p className="text-lg text-white/80">
              Sürdürülebilir arıcılık, ekosistem koruma ve topluluk geliştirme projelerimizi keşfedin.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filter */}
      <section className="py-8 bg-white sticky top-20 z-40 border-b border-[#E5DAC8]">
        <div className="container-custom">
          <div className="flex flex-wrap gap-4">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setFilter(cat.id)}
                className={`px-6 py-2 rounded-full font-medium transition-all ${
                  filter === cat.id 
                    ? 'bg-[#1A3625] text-white' 
                    : 'bg-[#F9F8F6] text-[#5C5C5C] hover:bg-[#E5DAC8]'
                }`}
                data-testid={`filter-${cat.id}`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="space-y-16">
            {filteredProjects.map((project, index) => (
              <motion.article
                key={project.id}
                id={project.id}
                className="card-premium"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                data-testid={`project-${project.id}`}
              >
                <div className="grid lg:grid-cols-2 gap-8">
                  <div className="aspect-video lg:aspect-auto rounded-2xl overflow-hidden">
                    <img 
                      src={project.image}
                      alt={project.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  
                  <div className="flex flex-col justify-center">
                    <div className="flex items-center gap-4 mb-4">
                      <span className={`px-4 py-1.5 rounded-full text-base font-medium ${
                        project.status === 'active' ? 'bg-[#1A3625]/10 text-[#1A3625]' : 
                        project.status === 'planned' ? 'bg-[#D4AF37]/20 text-[#8C7A6B]' :
                        'bg-[#D4AF37] text-white'
                      }`}>
                        {project.status === 'active' ? 'Aktif' : 
                         project.status === 'planned' ? 'Planlanan' : 'Tamamlandı'}
                      </span>
                      <span className="text-base text-[#8C7A6B] capitalize">{project.category}</span>
                    </div>
                    
                    <h2 className="text-xl md:text-2xl text-[#1A3625] mb-4">{project.title}</h2>
                    <p className="text-[#5C5C5C] mb-6">{project.description}</p>
                    
                    <div className="flex flex-wrap gap-4 mb-6 text-base">
                      <span className="flex items-center gap-2 text-[#5C5C5C]">
                        <MapPin className="w-4 h-4 text-[#D4AF37]" />
                        {project.location}
                      </span>
                      <span className="flex items-center gap-2 text-[#5C5C5C]">
                        <Calendar className="w-4 h-4 text-[#D4AF37]" />
                        {new Date(project.start_date).toLocaleDateString('tr-TR', { month: 'long', year: 'numeric' })}
                      </span>
                    </div>
                    
                    {project.goals && (
                      <div className="mb-6">
                        <h4 className="font-medium text-[#1A3625] mb-3 flex items-center gap-2">
                          <Target className="w-5 h-5 text-[#D4AF37]" />
                          Hedefler
                        </h4>
                        <ul className="space-y-2">
                          {project.goals.map((goal, i) => (
                            <li key={i} className="text-base text-[#5C5C5C] flex items-start gap-2">
                              <span className="w-1.5 h-1.5 bg-[#D4AF37] rounded-full mt-2 flex-shrink-0" />
                              {goal}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    <div className="bg-[#F9F8F6] rounded-2xl p-4">
                      <p className="text-base text-[#1A3625] font-medium">Etki:</p>
                      <p className="text-base text-[#5C5C5C]">{project.impact}</p>
                    </div>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
          
          {filteredProjects.length === 0 && (
            <div className="text-center py-16">
              <p className="text-[#5C5C5C]">Bu kategoride henüz proje bulunmuyor.</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-[#D4AF37]">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl text-[#1A3625] mb-4">
            Projelerimizi Destekleyin
          </h2>
          <p className="text-[#1A3625]/80 text-base mb-8 max-w-xl mx-auto">
            Sponsorluk veya gönüllülük ile projelerimize katkı sağlayabilirsiniz.
          </p>
          <a href="/katil" className="btn-primary inline-flex items-center gap-2">
            Destek Olun
            <ArrowRight className="w-5 h-5" />
          </a>
        </div>
      </section>
    </div>
  );
};
